import React from 'react';
import { FormField, Select, Button } from '../../../components';
import {
    FREQUENCY_OPTIONS, FREQUENCY_TIMES, INSTRUCTION_OPTIONS, TimesEditor,
    medTimes, frequencyFromTimes, scheduleFields,
} from './medicationTimes';
import { Clock, Pill, Plus, Trash2, FileText, UserSquare2, ChevronDown } from 'lucide-react';

// Step navigation lives in the ConvertClient page header, top-right.
const MedicalInfo = ({ updateData, data }) => {
    const medical = data.medicalDetails || {};
    const [showAddMedication, setShowAddMedication] = React.useState(false);

    // Helper to ensure we have an array
    const ensureArray = (val) => {
        if (!val) return [];
        if (Array.isArray(val)) return val;
        if (typeof val === 'string') {
            try { return JSON.parse(val); } catch (e) { return []; }
        }
        return [];
    };

    const medicationPlan = ensureArray(medical.medication_plan);
    const medicationHistory = ensureArray(medical.medication_history);

    // Unified state with extra details
    const [newMedication, setNewMedication] = React.useState({
        name: '',
        dosage: '',
        category: '',
        instruction: '',
        frequency: 'once',
        times: [...FREQUENCY_TIMES.once],
        days: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
        status: 'Inprogress',
        doctor: '',
        notes: ''
    });

    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const todayDayName = days[new Date().getDay()];

    const handleChange = (e) => {
        const { name, value } = e.target;
        updateData('medicalDetails', { [name]: value });
    };

    // Safely combine Plan and History for the unified UI View
    const allIds = Array.from(new Set([...medicationPlan.map(p => p.id), ...medicationHistory.map(h => h.id)]));
    const unifiedMedications = allIds.map(id => {
        const p = medicationPlan.find(x => x.id === id) || { days: [] };
        const h = medicationHistory.find(x => x.id === id) || { status: 'Inprogress', date: new Date().toISOString() };
        return { id, ...p, ...h };
    });

    const handleAddMedication = () => {
        if (!newMedication.name) return;
        const sharedId = Date.now();

        // 1. Keep Plan in Plan JSON
        const updatedPlan = [...medicationPlan, {
            id: sharedId,
            name: newMedication.name,
            dosage: newMedication.dosage,
            category: newMedication.category,
            instruction: newMedication.instruction,
            ...scheduleFields(newMedication.times),
            days: newMedication.days,
            doctor: newMedication.doctor,
            notes: newMedication.notes
        }];

        // 2. Keep History in History JSON
        const newHistoryEntry = {
            id: sharedId,
            name: newMedication.name,
            category: newMedication.category || 'General',
            date: new Date().toISOString().split('T')[0],
            status: newMedication.status
        };
        const updatedHistory = [...medicationHistory, newHistoryEntry];

        updateData('medicalDetails', {
            medication_plan: updatedPlan,
            medication_history: updatedHistory
        });

        // Reset form
        setNewMedication({ name: '', dosage: '', category: '', instruction: '', frequency: 'once', times: [...FREQUENCY_TIMES.once], days: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'], status: 'Inprogress', doctor: '', notes: '' });
        setShowAddMedication(false);
    };

    const updateMedicationStatus = (id, newStatus) => {
        const updatedHistory = medicationHistory.map(h => {
            if (h.id === id) return { ...h, status: newStatus };
            return h;
        });
        updateData('medicalDetails', { medication_history: updatedHistory });
    };

    const removeMedication = (id) => {
        const updatedPlan = medicationPlan.filter(p => p.id !== id);
        const updatedHistory = medicationHistory.filter(h => h.id !== id);
        updateData('medicalDetails', {
            medication_plan: updatedPlan,
            medication_history: updatedHistory
        });
    };

    // Updated colors: Blue for In Progress, Green for Completed, Red for Stopped
    const getStatusStyle = (status) => {
        switch (status) {
            case 'Stopped': return 'bg-red-50 dark:bg-red-900/10 text-red-600 dark:text-red-400 border-red-200 dark:border-red-900/50 focus:ring-red-500 focus:border-red-500';
            case 'Inprogress': return 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 border-blue-200 dark:border-blue-900/50 focus:ring-blue-500 focus:border-blue-500';
            case 'Completed': return 'bg-emerald-50 dark:bg-emerald-900/10 text-emerald-600 dark:text-emerald-400 border-emerald-200 dark:border-emerald-900/50 focus:ring-emerald-500 focus:border-emerald-500';
            default: return 'bg-gray-50 dark:bg-[#31373f] text-gray-600 dark:text-gray-400 border-gray-200 dark:border-gray-700';
        }
    };

    return (
        <div>
            <h2 className="text-[22px] font-bold text-[#334155] dark:text-gray-200 mb-8">Medical Information</h2>

            <div className="flex flex-col md:flex-row gap-10">
                {/* Left Column - Standard Medical Info */}
                <div className="flex-1 flex flex-col gap-6">
                    <div className="grid grid-cols-2 gap-5">
                        <FormField label="Primary Diagnosis" name="primary_diagnosis" value={medical.primary_diagnosis || ''} onChange={handleChange} placeholder="e.g. Dementia" />
                        <FormField label="Secondary Diagnosis" name="secondary_diagnosis" value={medical.secondary_diagnosis || ''} onChange={handleChange} placeholder="e.g. Hypertension" />
                    </div>

                    <div className="grid grid-cols-2 gap-5">
                        <FormField label="Resuscitation Status" name="resuscitation_status" value={medical.resuscitation_status || ''} onChange={handleChange} placeholder="e.g. DNAR" />
                        <FormField 
                            label="Mental Capacity" 
                            name="mental_capacity" 
                            type="select" 
                            value={medical.mental_capacity || ''} 
                            onChange={handleChange}
                            placeholder="Select Capacity"
                            options={[
                                { label: 'Has Capacity', value: 'Has Capacity' },
                                { label: 'Lacks Capacity', value: 'Lacks Capacity' },
                                { label: 'Fluctuating', value: 'Fluctuating' }
                            ]}
                        />
                    </div>

                    <FormField 
                        label="Dietary Requirements" 
                        name="dietary_requirements" 
                        type="select" 
                        value={medical.dietary_requirements || ''} 
                        onChange={handleChange}
                        placeholder="Select Dietary Needs"
                        options={[
                            { label: 'Normal', value: 'Normal' },
                            { label: 'Diabetic', value: 'Diabetic' },
                            { label: 'Soft / Puréed', value: 'Soft / Puréed' },
                            { label: 'Liquid', value: 'Liquid' }
                        ]}
                    />

                    <FormField 
                        label="Detailed Allergy Information" 
                        name="allergies_detail" 
                        type="textarea" 
                        rows={3} 
                        value={medical.allergies_detail || ''} 
                        onChange={handleChange} 
                        placeholder="List all medical and food allergies" 
                    />

                    <FormField 
                        label="Last Medical Review Date" 
                        name="last_medical_review_date" 
                        type="date" 
                        value={medical.last_medical_review_date || ''} 
                        onChange={handleChange} 
                    />

                    <FormField
                        label="Current Medications Overview"
                        name="current_medications_overview"
                        type="textarea"
                        rows={3}
                        value={medical.current_medications_overview || ''}
                        onChange={handleChange}
                        placeholder="General overview of current medication regime"
                    />

                    <FormField
                        label="What to tell the ambulance crew"
                        name="what_to_tell_crew"
                        type="textarea"
                        rows={3}
                        value={medical.what_to_tell_crew || ''}
                        onChange={handleChange}
                        placeholder="Key info for paramedics on arrival — shown on the emergency screen"
                    />
                </div>

                {/* Vertical Dashed Divider */}
                <div className="hidden md:block w-px border-l-2 border-dashed border-gray-200 dark:border-gray-700"></div>

                {/* Right Column - Contacts */}
                <div className="flex-1 flex flex-col gap-6">
                    <FormField label="Preferred Pharmacy" name="preferred_pharmacy_name" value={medical.preferred_pharmacy_name || ''} onChange={handleChange} placeholder="Pharmacy name & phone" />
                    <FormField label="Recent Hospital Admissions" name="recent_hospital_admissions" type="textarea" rows={2} value={medical.recent_hospital_admissions || ''} onChange={handleChange} placeholder="Dates and reasons" />

                    <div className="mt-2">
                        <h3 className="text-[14px] font-semibold text-[#475569] dark:text-gray-400 mb-4">External Medical Contacts</h3>

                        <div className="grid grid-cols-2 gap-4 mb-5">
                            <FormField label="Dentist Name" name="dentist_name" value={medical.dentist_name || ''} onChange={handleChange} placeholder="Dr. Name" />
                            <FormField label="Dentist Contact" name="dentist_contact" value={medical.dentist_contact || ''} onChange={handleChange} placeholder="Phone number" />
                        </div>

                        <div className="grid grid-cols-2 gap-4 mb-5">
                            <FormField label="Optician Name" name="optician_name" value={medical.optician_name || ''} onChange={handleChange} placeholder="Dr. Name" />
                            <FormField label="Optician Contact" name="optician_contact" value={medical.optician_contact || ''} onChange={handleChange} placeholder="Phone number" />
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <FormField label="Chiropodist Name" name="chiropodist_name" value={medical.chiropodist_name || ''} onChange={handleChange} placeholder="Dr. Name" />
                            <FormField label="Chiropodist Contact" name="chiropodist_contact" value={medical.chiropodist_contact || ''} onChange={handleChange} placeholder="Phone number" />
                        </div>
                    </div>
                </div>
            </div>

            {/* --- UNIFIED MEDICATION SECTION --- */}
            <div className="mt-12 bg-white dark:bg-[#262b33] rounded-3xl border border-gray-100 dark:border-gray-800 shadow-xl shadow-blue-50/50 overflow-hidden">
                <div className="p-8 border-b border-gray-100 dark:border-gray-800 bg-gradient-to-r from-gray-50/80 to-white flex flex-col sm:flex-row justify-between items-start sm:items-center gap-6">
                    <div>
                        <div className="flex items-center gap-3 mb-1">
                            <div className="p-2 bg-blue-600 rounded-lg">
                                <Pill className="w-5 h-5 text-white" />
                            </div>
                            <h2 className="text-[22px] font-bold text-[#1E293B] dark:text-gray-200">Medications & Plan</h2>
                        </div>
                        <p className="text-[14px] text-gray-500 dark:text-gray-400 font-medium">Comprehensive medication schedule and prescription history.</p>
                    </div>
                    <div className="flex items-center gap-4">
                        <div className="hidden md:flex items-center gap-6 px-6 py-2 bg-white dark:bg-[#31373f] rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm">
                            <div className="text-center">
                                <p className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest">Active</p>
                                <p className="text-[18px] font-bold text-blue-600">{unifiedMedications.filter(m => m.status === 'Inprogress').length}</p>
                            </div>
                            <div className="w-px h-8 bg-gray-100 dark:bg-gray-800"></div>
                            <div className="text-center">
                                <p className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest">Completed</p>
                                <p className="text-[18px] font-bold text-emerald-600">{unifiedMedications.filter(m => m.status === 'Completed').length}</p>
                            </div>
                        </div>
                        <button
                            onClick={() => setShowAddMedication(!showAddMedication)}
                            className="flex items-center gap-2 px-6 py-3 bg-[#2563EB] text-white rounded-2xl hover:bg-blue-700 transition-all hover:scale-[1.02] active:scale-[0.98] text-[15px] font-bold shadow-lg shadow-blue-200"
                        >
                            <Plus className="w-5 h-5" />
                            <span>Add Medicine</span>
                        </button>
                    </div>
                </div>

                {/* Unified Add Form */}
                {showAddMedication && (
                    <div className="px-8 py-7 bg-white dark:bg-[#262b33] border-b border-gray-100 dark:border-gray-800 animate-in fade-in zoom-in-95 duration-300">
                        {/* One 12-column grid for the whole form, so every field edge
                            lines up down the form and the row gaps stay even. */}
                        <div className="grid grid-cols-1 md:grid-cols-12 gap-x-5 gap-y-7">
                            <FormField
                                className="md:col-span-4"
                                size="lg"
                                label="Medicine Name"
                                placeholder="e.g. Paracetamol"
                                value={newMedication.name}
                                onChange={(e) => setNewMedication({ ...newMedication, name: e.target.value })}
                            />
                            <FormField
                                className="md:col-span-4"
                                size="lg"
                                label="Dosage"
                                placeholder="e.g. 500mg"
                                value={newMedication.dosage}
                                onChange={(e) => setNewMedication({ ...newMedication, dosage: e.target.value })}
                            />
                            <FormField
                                className="md:col-span-4"
                                size="lg"
                                label="Status"
                                type="select"
                                value={newMedication.status}
                                onChange={(e) => setNewMedication({ ...newMedication, status: e.target.value })}
                                options={[
                                    { value: 'Inprogress', label: 'In Progress' },
                                    { value: 'Completed', label: 'Completed' },
                                    { value: 'Stopped', label: 'Stopped' },
                                ]}
                            />

                            <FormField
                                className="md:col-span-3"
                                size="lg"
                                label="Category"
                                placeholder="e.g. Antibiotic"
                                value={newMedication.category}
                                onChange={(e) => setNewMedication({ ...newMedication, category: e.target.value })}
                            />
                            <FormField
                                className="md:col-span-6"
                                size="lg"
                                label="Instruction"
                                type="select"
                                value={newMedication.instruction}
                                onChange={(e) => setNewMedication({ ...newMedication, instruction: e.target.value })}
                                placeholder="Select Instruction..."
                                options={INSTRUCTION_OPTIONS.map(o => ({ label: o, value: o }))}
                            />
                            <FormField
                                className="md:col-span-3"
                                size="lg"
                                label="Frequency"
                                type="select"
                                value={newMedication.frequency}
                                onChange={(e) => setNewMedication({
                                    ...newMedication,
                                    frequency: e.target.value,
                                    times: [...(FREQUENCY_TIMES[e.target.value] || [])],
                                })}
                                options={FREQUENCY_OPTIONS.map(o => ({ label: o.label, value: o.value }))}
                            />

                            <div className="md:col-span-12">
                                <TimesEditor
                                    times={newMedication.times}
                                    onChange={(times) => setNewMedication({
                                        ...newMedication,
                                        times,
                                        frequency: frequencyFromTimes(times),
                                    })}
                                />
                            </div>

                            <FormField
                                className="md:col-span-6"
                                size="lg"
                                label="Prescribing Doctor"
                                placeholder="Dr. Name"
                                value={newMedication.doctor}
                                onChange={(e) => setNewMedication({ ...newMedication, doctor: e.target.value })}
                            />
                            <FormField
                                className="md:col-span-6"
                                size="lg"
                                label="Additional Notes"
                                placeholder="Enter notes..."
                                value={newMedication.notes}
                                onChange={(e) => setNewMedication({ ...newMedication, notes: e.target.value })}
                            />

                            {/* Same card treatment as the administration times above */}
                            <div className="md:col-span-12 border border-blue-100 dark:border-gray-700 rounded-xl p-3.5 bg-blue-50/30 dark:bg-[#20242c]">
                                <p className="flex items-center gap-1.5 text-[12px] font-semibold text-gray-600 dark:text-gray-300 mb-2.5">
                                    <Clock className="w-3.5 h-3.5 text-blue-600" />
                                    Schedule days
                                    <span className="font-normal text-gray-400 dark:text-gray-500">
                                        {newMedication.days.length === 7
                                            ? '— every day'
                                            : `— ${newMedication.days.length} day${newMedication.days.length === 1 ? '' : 's'} a week`}
                                    </span>
                                </p>
                                <div className="flex flex-wrap gap-2">
                                    {days.map(day => (
                                        <button
                                            key={day}
                                            type="button"
                                            onClick={() => {
                                                const updatedDays = newMedication.days.includes(day) ? newMedication.days.filter(d => d !== day) : [...newMedication.days, day];
                                                setNewMedication({ ...newMedication, days: updatedDays });
                                            }}
                                            className={`px-4 py-1.5 text-[12px] font-semibold rounded-lg border transition-colors ${
                                                newMedication.days.includes(day)
                                                    ? 'bg-blue-600 border-blue-600 text-white'
                                                    : 'bg-white dark:bg-[#262b33] border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 hover:border-blue-300 hover:text-blue-600'
                                            }`}
                                        >
                                            {day}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>

                        <div className="flex justify-end items-center gap-3 mt-7 pt-5 border-t border-gray-100 dark:border-gray-800">
                            <Button variant="ghost" onClick={() => setShowAddMedication(false)}>Cancel</Button>
                            <Button variant="primary" onClick={handleAddMedication}>Save to Plan & History</Button>
                        </div>
                    </div>
                )}
                <div className="p-6">
                    {/* Unified List View */}
                    <div className="space-y-4">
                        {unifiedMedications.length === 0 ? (
                            <div className="text-center py-16 bg-gray-50 dark:bg-[#20242c] rounded-2xl border-2 border-dashed border-gray-200 dark:border-gray-800">
                                <div className="w-16 h-16 bg-white dark:bg-[#31373f] rounded-full flex items-center justify-center mx-auto mb-4 shadow-sm">
                                    <Pill className="w-8 h-8 text-gray-300 dark:text-gray-600" />
                                </div>
                                <p className="text-gray-600 dark:text-gray-400 font-bold text-[16px]">No medications recorded yet</p>
                                <p className="text-gray-400 dark:text-gray-500 text-[14px] mt-1 max-w-[280px] mx-auto">Start building the medical plan by clicking the 'Add Medicine' button above.</p>
                            </div>
                        ) : (
                            unifiedMedications.map((item) => (
                                <div key={item.id} className="group relative bg-white dark:bg-[#262b33] border border-gray-100 dark:border-gray-800 rounded-2xl p-5 shadow-sm hover:shadow-md hover:border-blue-200 transition-all">
                                    <div className="flex flex-col lg:flex-row gap-6">
                                        
                                        {/* Main Info Block */}
                                        <div className="flex gap-4 min-w-[280px]">
                                            <div className={`w-14 h-14 rounded-2xl flex items-center justify-center shrink-0 transition-colors ${
                                                item.status === 'Completed' ? 'bg-emerald-50 dark:bg-emerald-900/10 text-emerald-600 dark:text-emerald-400' :
                                                item.status === 'Stopped' ? 'bg-red-50 dark:bg-red-900/10 text-red-600 dark:text-red-400' : 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400'
                                            }`}>
                                                <Pill className="w-7 h-7" />
                                            </div>
                                            <div className="flex flex-col justify-center">
                                                <h4 className="text-[17px] font-bold text-gray-900 dark:text-gray-100 group-hover:text-blue-600 transition-colors">{item.name}</h4>
                                                <div className="flex items-center gap-2 mt-1">
                                                    <span className="text-[12px] font-bold bg-gray-100 dark:bg-[#31373f] text-gray-600 dark:text-gray-400 px-2 py-0.5 rounded-md">{item.dosage}</span>
                                                    <span className="text-[12px] text-gray-500 dark:text-gray-400 font-medium">• {item.category}</span>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Timing & Schedule */}
                                        <div className="flex-1 flex flex-col justify-center border-l lg:border-l-0 lg:pl-0 pl-6 border-gray-100 dark:border-gray-800">
                                            <div className="flex items-center gap-2 mb-2">
                                                <div className="p-1.5 bg-amber-50 dark:bg-amber-900/10 rounded-lg">
                                                    <Clock className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                                                </div>
                                                <span className="text-[15px] font-bold text-gray-800 dark:text-gray-200">
                                                    {medTimes(item).length ? medTimes(item).join(' · ') : 'As required'}
                                                </span>
                                                <span className="text-[14px] text-gray-500 dark:text-gray-400 font-medium px-3 border-l border-gray-200 dark:border-gray-800 ml-1">{item.instruction}</span>
                                            </div>
                                            <div className="flex flex-wrap gap-1.5">
                                                {days.map(d => (
                                                    <span key={d} className={`text-[11px] font-bold uppercase tracking-tight px-2 py-0.5 rounded-md border transition-colors ${
                                                        item.days && item.days.includes(d) 
                                                            ? (d === todayDayName ? 'bg-blue-600 border-blue-600 text-white' : 'bg-blue-50 border-blue-100 text-blue-600')
                                                            : 'bg-gray-50 dark:bg-[#31373f] border-gray-100 dark:border-gray-800 text-gray-300 dark:text-gray-600'
                                                    }`}>
                                                        {d}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>

                                        {/* Meta Details */}
                                        <div className="hidden xl:flex flex-col justify-center min-w-[200px]">
                                            {item.doctor && (
                                                <div className="flex items-center gap-2 text-[13px] text-gray-600 dark:text-gray-400 mb-2">
                                                    <UserSquare2 className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                                                    <span className="font-medium">Dr. {item.doctor}</span>
                                                </div>
                                            )}
                                            {item.notes && (
                                                <div className="flex items-start gap-2 text-[13px] text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-[#20242c] p-2 rounded-lg">
                                                    <FileText className="w-4 h-4 text-gray-400 dark:text-gray-500 shrink-0 mt-0.5" />
                                                    <span className="line-clamp-2 leading-relaxed italic">"{item.notes}"</span>
                                                </div>
                                            )}
                                        </div>

                                        {/* Status & Actions */}
                                        <div className="flex items-center gap-4 shrink-0 lg:ml-auto pt-4 lg:pt-0 border-t lg:border-t-0 border-gray-100 dark:border-gray-800">
                                            <Select
                                                size="sm"
                                                className="w-[150px]"
                                                value={item.status}
                                                onChange={(v) => updateMedicationStatus(item.id, v)}
                                                options={[
                                                    { value: 'Inprogress', label: 'In Progress' },
                                                    { value: 'Completed', label: 'Completed' },
                                                    { value: 'Stopped', label: 'Stopped' },
                                                ]}
                                            />

                                            <button
                                                onClick={() => removeMedication(item.id)}
                                                className="p-2.5 text-gray-400 dark:text-gray-500 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all"
                                                title="Remove from plan"
                                            >
                                                <Trash2 className="w-5 h-5" />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            </div>

        </div>
    );
};

export default MedicalInfo;